"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { SidePanelLayout, SidePanelKind } from "src/components/ui/side-panel/SidePanelContext";

/** What a `SidePanel` tells the outlet it renders in. */
export interface SidePanelRegistration {
  id: string;
  kind: SidePanelKind;
  /**
   * Called when another panel of the same kind opened in this outlet. The outlet sets this panel
   * aside first (so its focus return does not steal focus from the new one), then asks it to close.
   */
  markReplaced: () => void;
  onReplaced: () => void;
  /** True while the panel holds unsaved work, so a newer panel of its kind must not close it. */
  isProtected: () => boolean;
}

/** One `SidePanelOutlet`, as panels and the provider see it. */
export interface SidePanelOutletHandle {
  id: string;
  /** 0 for the outermost outlet; each nested outlet adds one. The deepest one wins. */
  depth: number;
  /** The ids of the outlets this one is nested in, outermost first. */
  ancestorIds: string[];
  layout: SidePanelLayout;
  /** Register an open panel; returns the unregister function. Stable for the outlet's life. */
  registerPanel: (registration: SidePanelRegistration) => () => void;
  /** The element a panel of this kind portals into, or null before the outlet has mounted. */
  getSlot: (kind: SidePanelKind) => HTMLElement | null;
  /** False for a panel replaced by a newer one of the same kind, while it closes. */
  isPanelActive: (id: string) => boolean;
  /** True for an older panel kept, hidden, under a newer one of its kind for its unsaved work. */
  isPanelStacked: (id: string) => boolean;
  /** The page region, then each open, non-task panel nearest the page first. */
  getFocusRegions: () => HTMLElement[];
  /** Moves focus into one region: the page's last focused element, or a panel's title. */
  focusRegion: (region: HTMLElement) => void;
  /**
   * Tells the outlet how much of the page is not the content it must keep usable (a page's own
   * rail or column), so panels push only while the rest keeps `SIDE_PANEL_MIN_PAGE_WIDTH`.
   * Returns the function that withdraws it. `useSidePanelPageReserve` is the hook for pages.
   */
  reservePageWidth?: (width: number) => () => void;
}

interface SidePanelProviderValue {
  announce: (text: string) => void;
  /** Adds an outlet; returns the unregister function. */
  registerOutlet: (outlet: SidePanelOutletHandle) => () => void;
  /** Replaces a registered outlet's handle in place, keeping its order. */
  updateOutlet: (outlet: SidePanelOutletHandle) => void;
  /**
   * Counts an open modal panel (a task, or any panel covering the screen below 768px); returns the
   * function that uncounts it. While any is open, app chrome outside the outlets is inert.
   */
  registerModal: () => () => void;
  /** Adds an element of app chrome (`SidePanelChrome`); returns the function that removes it. */
  registerChrome: (element: HTMLElement) => () => void;
}

const SidePanelProviderContext = createContext<SidePanelProviderValue | null>(null);

/**
 * The deepest registered outlet, for panels rendered outside every outlet. A context of its own, so
 * an outlet's changes re-render only those panels, not everything that reads the provider.
 */
const InnermostOutletContext = createContext<SidePanelOutletHandle | null>(null);

export function useSidePanelProvider(): SidePanelProviderValue | null {
  return useContext(SidePanelProviderContext);
}

export function useInnermostSidePanelOutlet(): SidePanelOutletHandle | null {
  return useContext(InnermostOutletContext);
}

const noop = () => undefined;

/**
 * Says something once through the provider's polite live region ("Showing Turf area, Ballston
 * Annex"). A no-op outside a `SidePanelProvider`.
 */
export function useSidePanelAnnounce(): (text: string) => void {
  return useSidePanelProvider()?.announce ?? noop;
}

/**
 * App chrome outside the side panel outlets, such as the sidebar and the project navbar. A task
 * panel's backdrop dims only its outlet, so the chrome stays in view; while a task (or a panel
 * covering a phone screen) is open it is inert, so a click there cannot navigate away and drop the
 * task's unsaved edits.
 */
export function SidePanelChrome({ children }: Readonly<{ children: ReactNode }>) {
  const registerChrome = useSidePanelProvider()?.registerChrome;
  const chromeRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const chrome = chromeRef.current;
    if (!chrome || !registerChrome) return undefined;
    return registerChrome(chrome);
  }, [registerChrome]);
  return (
    <div ref={chromeRef} data-side-panel-chrome="" className="contents">
      {children}
    </div>
  );
}

/** Delay between clearing and writing the live region, so repeating a message is announced. */
const ANNOUNCE_DELAY_MS = 50;

/**
 * App-wide home for side panels: the registry of `SidePanelOutlet`s (the deepest one wins for
 * panels rendered outside every outlet), one polite live region (`announce`), and F6 / Shift+F6,
 * which move focus between the page and the open panels without closing them.
 */
export function SidePanelProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [outlets, setOutlets] = useState<SidePanelOutletHandle[]>([]);
  const outletsRef = useRef(outlets);
  useLayoutEffect(() => {
    outletsRef.current = outlets;
  });
  const [message, setMessage] = useState("");
  const announceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const announce = useCallback((text: string) => {
    if (announceTimerRef.current) clearTimeout(announceTimerRef.current);
    setMessage("");
    announceTimerRef.current = setTimeout(() => {
      announceTimerRef.current = null;
      setMessage(text);
    }, ANNOUNCE_DELAY_MS);
  }, []);

  useEffect(
    () => () => {
      if (announceTimerRef.current) clearTimeout(announceTimerRef.current);
    },
    [],
  );

  const registerOutlet = useCallback((outlet: SidePanelOutletHandle) => {
    setOutlets((current) => [...current.filter((entry) => entry.id !== outlet.id), outlet]);
    return () => setOutlets((current) => current.filter((entry) => entry.id !== outlet.id));
  }, []);

  const updateOutlet = useCallback((outlet: SidePanelOutletHandle) => {
    setOutlets((current) => {
      const index = current.findIndex((entry) => entry.id === outlet.id);
      if (index === -1 || current[index] === outlet) return current;
      const next = [...current];
      next[index] = outlet;
      return next;
    });
  }, []);

  // Chrome is made inert directly rather than through render state: when the last modal panel
  // closes, its focus return runs in the same commit, and must find the opener (often a navbar
  // button) already focusable.
  const modalCountRef = useRef(0);
  const chromeElementsRef = useRef(new Set<HTMLElement>());
  const syncChromeInert = useCallback(() => {
    const isInert = modalCountRef.current > 0;
    chromeElementsRef.current.forEach((element) => {
      if (isInert) element.setAttribute("inert", "");
      else element.removeAttribute("inert");
    });
  }, []);
  const registerModal = useCallback(() => {
    modalCountRef.current += 1;
    syncChromeInert();
    return () => {
      modalCountRef.current -= 1;
      syncChromeInert();
    };
  }, [syncChromeInert]);
  const registerChrome = useCallback(
    (element: HTMLElement) => {
      chromeElementsRef.current.add(element);
      syncChromeInert();
      return () => {
        chromeElementsRef.current.delete(element);
        element.removeAttribute("inert");
      };
    },
    [syncChromeInert],
  );

  // F6 / Shift+F6: page region and panels, in the deepest outlet that has a panel open.
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "F6" || event.defaultPrevented) return;
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      // A task panel is modal and keeps focus; F6 has nowhere else to go.
      if (document.querySelector('[data-side-panel][data-kind="task"]')) return;
      const byDepth = [...outletsRef.current].sort((a, b) => b.depth - a.depth);
      for (const outlet of byDepth) {
        const regions = outlet.getFocusRegions();
        if (regions.length < 2) continue;
        const active = document.activeElement;
        const current = regions.findIndex((region) => active && region.contains(active));
        let next: number;
        if (current === -1) next = event.shiftKey ? regions.length - 1 : 1;
        else next = (current + (event.shiftKey ? regions.length - 1 : 1)) % regions.length;
        event.preventDefault();
        outlet.focusRegion(regions[next]);
        return;
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Ties in depth go to the outlet registered first, which never changes while both are mounted.
  const innermostOutlet = useMemo(
    () =>
      outlets.reduce<SidePanelOutletHandle | null>(
        (deepest, outlet) => (!deepest || outlet.depth > deepest.depth ? outlet : deepest),
        null,
      ),
    [outlets],
  );

  const value = useMemo<SidePanelProviderValue>(
    () => ({ announce, registerOutlet, updateOutlet, registerModal, registerChrome }),
    [announce, registerChrome, registerModal, registerOutlet, updateOutlet],
  );

  return (
    <SidePanelProviderContext.Provider value={value}>
      <InnermostOutletContext.Provider value={innermostOutlet}>
        {children}
      </InnermostOutletContext.Provider>
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        data-side-panel-announcer=""
        className="sr-only"
      >
        {message}
      </div>
    </SidePanelProviderContext.Provider>
  );
}
